import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FiTag, FiAlignLeft, FiSave, FiArrowLeft, FiCheckCircle } from "react-icons/fi";
import { 
  addCategory, 
  updateCategory, 
  getCategoryById 
} from "../services/categoryService";

const AddCategoryLayer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState({
    name: "",
    description: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isEdit && id) {
      loadCategory();
    }
  }, [id]);

  const loadCategory = async () => {
    try {
      const res = await getCategoryById(id);
      if (res?.status !== "success") {
        toast.error(res?.message || "Category not found");
        return;
      }
      const category = res.category;
      setForm({
        name: category.name || "",
        description: category.description || "",
      });
    } catch (error) {
      console.error(error);
      toast.error("Error loading category analytics");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    let temp = {};
    if (!form.name.trim()) temp.name = "Strategic name required";
    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please fill required tactical fields");
      return;
    }

    setLoading(true);
    try {
      const res = isEdit
        ? await updateCategory(id, form)
        : await addCategory(form);

      if (res?.status === "success") {
        toast.success(res.message || "Matrix node updated successfully");
        navigate("/category-list");
      } else {
        toast.error(res?.message || "Deployment failed");
      }
    } catch (error) {
      console.error(error);
      toast.error("Intelligence Link Error");
    }
    setLoading(false);
  };

  return (
    <div className="container mt-4 mb-5 px-3 px-lg-0">
      <div className="row justify-content-center">
        <div className="col-12 col-lg-8 col-xl-7">
          <div className="card shadow-premium border-0 animate-fade-in-up overflow-hidden rounded-4">
            {/* HERDER */}
            <div className="card-header bg-primary py-4 px-4 border-0 d-flex justify-content-between align-items-center">
               <div className="d-flex align-items-center gap-3 text-white">
                  <div className="bg-white bg-opacity-20 p-2 rounded-3">
                     <FiTag size={24} />
                  </div>
                  <div>
                     <h4 className="mb-0 fw-bold">{isEdit ? "Refine Category" : "Initialize Category"}</h4>
                     <p className="extra-small opacity-75 mb-0">Structuring systemic categorization nodes</p>
                  </div>
               </div>
               <Link to="/category-list" className="btn btn-white-soft rounded-pill btn-sm px-3 fw-bold">
                  <FiArrowLeft className="me-2"/> Back to Registry
               </Link>
            </div>

            <div className="card-body p-4 p-md-5 bg-white">
              <form onSubmit={handleSubmit} className="premium-modern-form">
                <div className="row g-4">
                  {/* CATEGORY NAME */}
                  <div className="col-12">
                    <label className="form-label text-muted small fw-bold d-flex align-items-center gap-2">
                       <FiTag className="text-primary"/> Category Designation *
                    </label>
                    <input
                      type="text"
                      name="name"
                      placeholder="e.g. Strategic Management, Tactical Operations"
                      value={form.name}
                      onChange={handleChange}
                      className={`form-control form-control-lg border-0 bg-light-soft rounded-3 px-4 ${errors.name ? 'is-invalid' : ''}`}
                    />
                    {errors.name && (
                      <div className="invalid-feedback extra-small fw-bold">{errors.name}</div>
                    )}
                  </div>

                  {/* DESCRIPTION */}
                  <div className="col-12">
                    <label className="form-label text-muted small fw-bold d-flex align-items-center gap-2">
                       <FiAlignLeft className="text-primary"/> Functional Description
                    </label>
                    <textarea
                      name="description"
                      rows="5"
                      placeholder="Define the scope and strategic boundaries of this node..."
                      value={form.description}
                      onChange={handleChange}
                      className="form-control border-0 bg-light-soft rounded-3 px-4 py-3"
                    />
                  </div>

                  {/* SUBMIT BUTTON */}
                  <div className="col-12 mt-5">
                    <button
                      type="submit"
                      disabled={loading}
                      className="btn btn-primary-gradient w-100 py-3 rounded-pill shadow-sm fw-bold d-flex align-items-center justify-content-center gap-2"
                    >
                      {loading ? (
                        <div className="spinner-border spinner-border-sm" role="status"></div>
                      ) : (
                        <>
                          <FiSave />
                          {isEdit ? "Synchronize Matrix Node" : "Deploy New Category Node"}
                        </>
                      )}
                    </button>
                    
                    <div className="text-center mt-3">
                       <span className="extra-small text-muted d-flex align-items-center justify-content-center gap-2">
                          <FiCheckCircle className="text-success"/> System validated input required
                       </span>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddCategoryLayer;
